"use client";

import React from "react";
import { ArrowUpRight, Gauge, PlugZap, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
interface SetupCard {
  id: string;
  title: string;
  description: string;
}
const setupCards: SetupCard[] = [{
  id: "space",
  title: "Space",
  description: "Check the complete operating dimensions for the exact bull and inflatable rather than measuring only the mechanical base."
}, {
  id: "power",
  title: "Power",
  description: "Confirm the electrical requirements for the exact machine, controller and blower before planning installation."
}, {
  id: "access",
  title: "Access",
  description: "Consider doors, gates, loading areas, packed dimensions and unloading requirements before delivery."
}];
export default function Setup() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="setup-power-space" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-10 lg:px-16">
        {/* Section Header */}
        <div className="border-b border-border pb-8">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 bg-primary" />
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  Facility Readiness & Layout
                </span>
              </div>
              <h2 className="mt-3 font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl lg:text-5xl">
                Setup, power and space
              </h2>
              <p className="mt-3 max-w-2xl font-sans text-base text-muted-foreground sm:text-lg">
                Mechanical bull setup requirements vary by machine.
                Before ordering, confirm three things: space, power and access.
              </p>
            </div>

            <div>
              <Button asChild variant="outline" className="h-11 rounded-none border-foreground font-serif text-sm font-semibold tracking-wider uppercase text-foreground hover:bg-foreground hover:text-background">
                <Link to="/SetupSafetyAndMaintenancePage" className="inline-flex items-center gap-2">
                  <span>READ SETUP & SAFETY GUIDE</span>
                  <ArrowUpRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* 3-Column Industrial Spec Plates */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {setupCards.map((item, i) => {
          const Icon = item.id === "space" ? Gauge : item.id === "power" ? PlugZap : Wrench;
          return <motion.article key={item.id} data-index={i} variants={cardVariants} className="group relative flex flex-col justify-between border border-border bg-card p-6 transition-[border-color] duration-200 hover:border-foreground">
                <div>
                  {/* Plate Header */}
                  <div className="flex items-center justify-between border-b border-border pb-4">
                    <div className="flex size-9 items-center justify-center border border-border bg-muted text-foreground">
                      <Icon className="size-4.5 text-foreground" />
                    </div>
                    <span className="font-mono text-xs text-muted-foreground">
                      INDEX 0{i + 1}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="mt-5 font-serif text-2xl font-semibold tracking-wide text-foreground uppercase">
                    {item.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-4 font-sans text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {item.description}
                  </p>
                </div>
              </motion.article>;
        })}
        </motion.div>
      </div>
    </section>;
}